import { Add, TrashCan } from '@carbon/icons-react';
import {
  Button,
  Checkbox,
  ContentSwitcher,
  FilterableMultiSelect,
  FormGroup,
  InlineLoading,
  InlineNotification,
  Layer,
  MenuButton,
  MenuItem,
  MenuItemGroup,
  Switch,
  Tag,
} from '@carbon/react';
import { useTranslation, type I18nKey } from '@pos/i18n';
import { useQuery } from '@tanstack/react-query';
import { Fragment, useMemo } from 'react';
import { useAdmin, type FieldEditorProps } from '../adminContext';
import { FieldInput } from '../fields/FieldInput';
import { useReferenceOptions } from '../fields/references';
import type { FieldDef, FieldType } from '../resources';
import {
  countConditions,
  defaultParams,
  nextId,
  parseRules,
  serializeRules,
  type ConditionNode,
  type GroupNode,
  type RuleConditionType,
  type RuleNode,
  type RuleParam,
} from './ruleModel';
import './RuleBuilder.scss';

/** What the rules are for: which conditions HO's catalogue offers (`reward`: a Rewards program's member conditions, ADR 0079). */
export type RuleKind = 'charge' | 'preset' | 'promotion' | 'voucher' | 'points' | 'reward' | 'segment';

interface LooseClient {
  GET(path: string, init?: object): Promise<{ data?: unknown; error?: unknown; response: Response }>;
}

/** The conditions a charge's or a preset's rules can use, from HO (what makes the builder extensible without UI code). */
function useRuleConditions(kind: RuleKind) {
  const { client } = useAdmin();
  return useQuery({
    queryKey: ['rule-conditions', kind],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error, response } = await (client as LooseClient).GET('/api/v1/menu/config/rule-conditions', {
        params: { query: { kind } },
      });
      if (!response.ok) throw error ?? new Error(String(response.status));
      return data as RuleConditionType[];
    },
  });
}

const PARAM_FIELD: Record<string, FieldType> = {
  integer: 'number',
  money: 'decimal',
  quantity: 'decimal',
  time: 'time',
  days: 'weekdays',
  date: 'date',
  reference: 'reference',
};

/** The order condition groups are offered in: where, when, the order, its items; any other after them. */
const GROUP_ORDER = ['where', 'when', 'order', 'items', 'member', 'customer', 'habits'];
const groupRank = (group: string) => (GROUP_ORDER.includes(group) ? GROUP_ORDER.indexOf(group) : GROUP_ORDER.length);

/**
 * When a charge applies or a discount preset can be used (ADR 0039), edited as conditions instead of JSON. The conditions
 * and their parameters come from HO's rule catalogue, so a condition added there shows up here without UI changes.
 *
 * Carbon: each condition is a bordered panel on the next layer (its fields take that layer's colour); "and" / "or" tags join
 * them; conditions are added from a `MenuButton`, grouped by what they look at.
 */
export function RuleBuilder({ field, value, invalidText, onChange, kind }: FieldEditorProps & { kind: RuleKind }) {
  const { t } = useTranslation();
  const conditions = useRuleConditions(kind);
  const parsed = useMemo(() => {
    try {
      return { root: parseRules(value) };
    } catch (error) {
      return { error: error instanceof Error ? error.message : String(error) };
    }
  }, [value]);
  const types = conditions.data ?? [];
  const typeOf = (type: string) => types.find((c) => c.type === type);
  const label = (type: string) => t(`rules.conditions.${type}` as I18nKey, { defaultValue: type });
  const groupLabel = (group: string) => t(`rules.groups.${group}` as I18nKey, { defaultValue: group });
  const groups = [...new Set(types.map((c) => c.group))].sort((a, b) => groupRank(a) - groupRank(b));

  if (conditions.isLoading) return <InlineLoading description={t('rules.builder.loading')} />;
  if (!parsed.root) {
    // Rules the builder can't show (written by hand or by a newer version) are left as they are.
    return (
      <InlineNotification
        kind="warning"
        lowContrast
        hideCloseButton
        title={t('rules.builder.invalid', { problem: parsed.error })}
      />
    );
  }

  const root = parsed.root;
  const save = (next: GroupNode) => onChange(serializeRules(next));
  const addTo = (group: GroupNode, type: RuleConditionType): GroupNode => ({
    ...group,
    nodes: [
      ...group.nodes,
      { kind: 'condition', id: nextId(root), type: type.type, params: defaultParams(type), exclude: false },
    ],
  });
  const replace = (group: GroupNode, index: number, node: RuleNode | null): GroupNode => ({
    ...group,
    nodes: node ? group.nodes.map((n, i) => (i === index ? node : n)) : group.nodes.filter((_, i) => i !== index),
  });

  const renderGroup = (group: GroupNode, update: (next: GroupNode) => void, nested: boolean) => (
    <div className="pos-rules__group">
      {group.nodes.length > 1 && (
        <div className="pos-rules__match">
          <span className="pos-rules__match-label">{t('rules.builder.match')}</span>
          <div className="pos-rules__match-switch">
            <ContentSwitcher
              size="md"
              selectedIndex={group.match === 'all' ? 0 : 1}
              onChange={({ index }) => update({ ...group, match: index === 0 ? 'all' : 'any' })}
            >
              <Switch name="all" text={t('rules.builder.all')} />
              <Switch name="any" text={t('rules.builder.any')} />
            </ContentSwitcher>
          </div>
        </div>
      )}
      {group.nodes.map((node, index) => (
        <Fragment key={node.kind === 'group' ? `g${index}` : node.id}>
          {index > 0 && (
            <div className="pos-rules__joint">
              <Tag type="cool-gray" size="sm">
                {t(group.match === 'all' ? 'rules.builder.and' : 'rules.builder.or')}
              </Tag>
            </div>
          )}
          {node.kind === 'group' ? (
            <Layer className="pos-rules__item pos-rules__item--group">
              <div className="pos-rules__item-head">
                <h4 className="pos-rules__item-title">{t('rules.builder.group')}</h4>
                <Button
                  kind="ghost"
                  size="sm"
                  hasIconOnly
                  renderIcon={TrashCan}
                  iconDescription={t('rules.builder.remove', { name: t('rules.builder.group') })}
                  tooltipPosition="left"
                  onClick={() => update(replace(group, index, null))}
                />
              </div>
              {renderGroup(node, (next) => update(replace(group, index, next)), true)}
            </Layer>
          ) : (
            <ConditionEditor
              node={node}
              type={typeOf(node.type)}
              label={label(node.type)}
              onChange={(next) => update(replace(group, index, next))}
            />
          )}
        </Fragment>
      ))}
      <div className="pos-rules__add">
        <MenuButton label={t('rules.builder.addCondition')} kind="tertiary" size="md" menuAlignment="bottom-start">
          {groups.map((name) => (
            <MenuItemGroup key={name} label={groupLabel(name)}>
              {types
                .filter((c) => c.group === name)
                .map((c) => (
                  <MenuItem key={c.type} label={label(c.type)} onClick={() => update(addTo(group, c))} />
                ))}
            </MenuItemGroup>
          ))}
        </MenuButton>
        {!nested && (
          <Button
            kind="ghost"
            size="md"
            renderIcon={Add}
            onClick={() => update({ ...group, nodes: [...group.nodes, { kind: 'group', match: 'any', nodes: [] }] })}
          >
            {t('rules.builder.addGroup')}
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <FormGroup legendText={t(field.label)} className="pos-rules" invalid={Boolean(invalidText)}>
      {countConditions(root) === 0 && (
        <div className="pos-rules__always">
          <p className="pos-rules__always-title">{t('rules.builder.always')}</p>
          <p className="pos-rules__always-hint">{t('rules.builder.alwaysHint')}</p>
        </div>
      )}
      {renderGroup(root, save, false)}
      {invalidText && <InlineNotification kind="error" lowContrast hideCloseButton title={invalidText} />}
    </FormGroup>
  );
}

function ConditionEditor({
  node,
  type,
  label,
  onChange,
}: {
  node: ConditionNode;
  type: RuleConditionType | undefined;
  label: string;
  onChange(next: ConditionNode | null): void;
}) {
  const { t } = useTranslation();
  const set = (name: string, value: unknown) => onChange({ ...node, params: { ...node.params, [name]: value } });
  return (
    <Layer className={node.exclude ? 'pos-rules__item pos-rules__item--excluded' : 'pos-rules__item'}>
      <div className="pos-rules__item-head">
        <h4 className="pos-rules__item-title">{label}</h4>
        {node.exclude && (
          <Tag type="red" size="sm">
            {t('rules.builder.excluded')}
          </Tag>
        )}
        <Checkbox
          id={`rule-${node.id}-exclude`}
          labelText={t('rules.builder.exclude')}
          checked={node.exclude}
          onChange={(_event, { checked }) => onChange({ ...node, exclude: checked })}
        />
        <Button
          kind="ghost"
          size="sm"
          hasIconOnly
          renderIcon={TrashCan}
          iconDescription={t('rules.builder.remove', { name: label })}
          tooltipPosition="left"
          onClick={() => onChange(null)}
        />
      </div>
      <div className="pos-rules__params">
        {(type?.parameters ?? []).map((param) =>
          param.kind === 'reference' && param.multiple ? (
            <ReferencesInput
              key={param.name}
              id={`rule-${node.id}-${param.name}`}
              param={param}
              value={node.params[param.name]}
              onChange={(v) => set(param.name, v)}
            />
          ) : (
            <div key={param.name} className={param.kind === 'days' ? 'pos-rules__param--wide' : undefined}>
              <FieldInput
                field={paramField(node.id, param)}
                value={fromStored(param, node.params[param.name])}
                onChange={(v) => set(param.name, toStored(param, v))}
              />
            </div>
          ),
        )}
      </div>
    </Layer>
  );
}

/** Several records of a resource, e.g. the sale types a charge applies to. */
function ReferencesInput({
  id,
  param,
  value,
  onChange,
}: {
  id: string;
  param: RuleParam;
  value: unknown;
  onChange(ids: string[]): void;
}) {
  const { t } = useTranslation();
  const options = useReferenceOptions(param.reference ?? undefined);
  const items = options.data ?? [];
  const chosen = Array.isArray(value) ? (value as string[]) : [];
  const selected = items.filter((o) => chosen.includes(o.value));
  // The picker shows only how many are chosen; their names stay in view underneath.
  return (
    <div className="pos-rules__references pos-rules__param--wide">
      <FilterableMultiSelect<{ value: string; label: string }>
        id={id}
        titleText={t(`rules.params.${param.name}` as I18nKey, { defaultValue: param.name })}
        placeholder={t('rules.builder.choose')}
        items={items}
        itemToString={(o) => o?.label ?? ''}
        selectedItems={selected}
        onChange={({ selectedItems }) => onChange((selectedItems ?? []).map((o) => o.value))}
        selectionFeedback="fixed"
      />
      {selected.length > 0 && (
        <div className="pos-rules__chosen">
          {selected.map((o) => (
            <Tag key={o.value} type="gray">
              {o.label}
            </Tag>
          ))}
        </div>
      )}
    </div>
  );
}

function paramField(conditionId: string, param: RuleParam): FieldDef {
  return {
    key: `rule-${conditionId}-${param.name}`,
    label: `rules.params.${param.name}` as I18nKey,
    type: PARAM_FIELD[param.kind] ?? 'text',
    required: param.required,
    ref: param.reference ?? undefined,
  };
}

/** Times are stored as HH:mm; the time input works in HH:mm:ss. */
const fromStored = (param: RuleParam, value: unknown) =>
  param.kind === 'time' && typeof value === 'string' ? `${value}:00` : value;
const toStored = (param: RuleParam, value: unknown) =>
  param.kind === 'time' && typeof value === 'string' ? value.slice(0, 5) : value;
